import { Injectable, Inject } from '@angular/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Resource, Product, Wishlist } from 'catalean-models';
import { ANALYTICS_CONFIG, AnalyticsConfig } from './analytics.config';
import { AnalyticsEventType, ContentType, ContentViewEvent, ContentDownloadEvent, UserLoginEvent, NavigationEvent, SharedLinkOpenEvent } from './analytics.models';
import { MyResource } from 'src/app/models/tecnopool-product';
import { Capacitor } from '@capacitor/core';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';

@Injectable({
	providedIn: 'root',
})
export class AnalyticsService {

	private attDenied = false;
	private attStatusInitialized = false;

	constructor(
		@Inject(ANALYTICS_CONFIG) private config: AnalyticsConfig,
		private gtmService: GoogleTagManagerService
	) {
		this.initATTStatus();
	}

	/**
	 * Inizializza lo stato ATT in modo asincrono.
	 * Viene chiamato una volta nel constructor e il risultato viene cachato.
	 */
	private async initATTStatus(): Promise<void> {
		if (Capacitor.getPlatform() !== 'ios') {
			this.attStatusInitialized = true;
			return;
		}
		try {
			const { status } = await AppTrackingTransparency.getStatus();
			this.attDenied = status === 'denied' || status === 'restricted';
		} catch {
			this.attDenied = false;
		} finally {
			this.attStatusInitialized = true;
		}
	}

  hasCookiesDisabled(): boolean {
    // Su iOS nativo: controlla ATT
    if (Capacitor.getPlatform() === 'ios') {
      try {
        // Finche' ATT non e' stato letto, blocca in modo conservativo l'invio analytics
        if (!this.attStatusInitialized) {
          return true;
        }

        // Controlla in modo sincrono tramite cache locale
        if (this.attDenied) {
          return true;
        }
      } catch {}
    }

    try {
      const w = window as any;
      // Controlla se iubenda CS è caricato e se il consenso è stato dato
      if (w._iub?.cs?.api) {
        return !w._iub.cs.api.isConsentGiven();
      }
      // Fallback: controlla il cookie di iubenda
      const cookieName = `_iub_cs-${this.config.iubendaSiteId}`;
      const cookie = document.cookie.split(';').find(c => c.trim().startsWith(cookieName + '='));
      if (cookie) {
        const value = JSON.parse(decodeURIComponent(cookie.split('=')[1]));
        return value.consent !== true;
      }
      // Se non c'è ancora il cookie, il consenso non è stato dato
      return true;
    } catch {
      return true;
    }
  }

	/**
	 * Metodo generico per inviare eventi al dataLayer
	 */
	private pushEvent(eventName: string, eventData: any): void {
		if (!this.config.enabled || this.hasCookiesDisabled()) {
			return;
		}

		const enrichedData = {
			event: eventName,
			...eventData,
			timestamp: Date.now(),
			environment: this.config.environment,
		};

		// Usa il servizio GTM della libreria per pushare eventi
		this.gtmService.pushTag(enrichedData);

		if (this.config.debug) {
			// console.log('[Analytics] Event pushed:', eventName, enrichedData);
		}
	}

	/**
	 * Determina il tipo di contenuto dall'estensione/format
	 */
	getContentTypeFromResource(resource: Resource): ContentType {
		const ext = resource.format?.toLowerCase() || '';

		if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
			return ContentType.IMAGE;
		}
		if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', '3gp'].includes(ext)) {
			return ContentType.VIDEO;
		}
		return ContentType.DOCUMENT;
	}

	/**
	 * TRACCIAMENTO VISUALIZZAZIONE CONTENUTO
	 */
	trackContentView<T extends { uuid: string, name?: string, url: string, format: string }>(resource: Resource, product?: Product): void {
		const eventData: ContentViewEvent = {
			event_type: AnalyticsEventType.CONTENT_VIEW,
			content_id: resource.uuid,
			content_name: resource.name || 'Unknown',
			content_url: resource.url,
			content_type: this.getContentTypeFromResource(resource),
			product_id: product?.uuid,
			product_name: product?.name,
			file_extension: resource.format,
			timestamp: Date.now(),
		};

		this.pushEvent(AnalyticsEventType.CONTENT_VIEW, eventData);
	}
  trackContentViewWithResource(resource: MyResource, contentType: ContentType, product?: Product): void {
		const eventData: ContentViewEvent = {
			event_type: AnalyticsEventType.CONTENT_VIEW,
			content_id: resource.uuid,
			content_name: resource.name || 'Unknown',
			content_url: resource.url,
			content_type: contentType,
			product_id: product?.uuid,
			product_name: product?.name,
			timestamp: Date.now(),
		};

		this.pushEvent(AnalyticsEventType.CONTENT_VIEW, eventData);
	}

	/**
	 * TRACCIAMENTO DOWNLOAD CONTENUTO
	 */
	trackContentDownload(resource: Resource, product?: Product, downloadMethod: 'direct' | 'browser' = 'direct'): void {
		const eventData: ContentDownloadEvent = {
			event_type: AnalyticsEventType.CONTENT_DOWNLOAD,
			content_id: resource.uuid,
			content_name: resource.name || 'Unknown',
			content_type: this.getContentTypeFromResource(resource),
			content_url: resource.url,
			product_id: product?.uuid,
			product_name: product?.name,
			file_extension: resource.format,
			file_size: resource.bytes ? parseInt(resource.bytes, 10) : undefined,
			download_method: downloadMethod,
			timestamp: Date.now(),
		};

		this.pushEvent(AnalyticsEventType.CONTENT_DOWNLOAD, eventData);
	}
  trackContentDownloadWithResource(resource: MyResource, contentType: ContentType, product?: Product, downloadMethod: 'direct' | 'browser' = 'direct'): void {
		const eventData: ContentDownloadEvent = {
			event_type: AnalyticsEventType.CONTENT_DOWNLOAD,
			content_id: resource.uuid,
			content_name: resource.name || 'Unknown',
			content_type: contentType,
			content_url: resource.url,
			product_id: product?.uuid,
			product_name: product?.name,
			download_method: downloadMethod,
			timestamp: Date.now(),
		};

		this.pushEvent(AnalyticsEventType.CONTENT_DOWNLOAD, eventData);
	}

	/**
	 * TRACCIAMENTO ACCESSO UTENTE
	 */
	trackUserLogin(platform: 'ios' | 'android' | 'desktop' = 'desktop'): void {
		const eventData: UserLoginEvent = {
			event_type: AnalyticsEventType.USER_LOGIN,
			login_time: Date.now(),
			device_type: this.getDeviceType(),
			platform: platform,
			timestamp: Date.now(),
		};

		this.pushEvent(AnalyticsEventType.USER_LOGIN, eventData);
	}

	/**
	 * Determina il tipo di dispositivo
	 */
	private getDeviceType(): 'mobile' | 'tablet' | 'desktop' | 'electron' {
		const ua = navigator.userAgent.toLowerCase();

		if (ua.includes('electron')) return 'electron';
		if (ua.includes('tablet') || ua.includes('ipad')) return 'tablet';
		if (ua.includes('mobile') || ua.includes('android')) return 'mobile';
		return 'desktop';
	}

	/**
	 * TRACCIAMENTO NAVIGAZIONE
	 */
	trackNavigation(page: string): void {
		const eventData: NavigationEvent = {
			event_type: AnalyticsEventType.NAVIGATION,
			page,
			timestamp: Date.now(),
		};

		this.pushEvent(AnalyticsEventType.NAVIGATION, eventData);
	}

    /**
   * TRACCIAMENTO APERTURA LINK CONDIVISO
   */
  trackSharedLinkOpen(
    contentId: string,
    contentType: 'wishlist' | 'unknown',
    contentName: string = 'unknown',
    isAuthenticated: boolean = false
  ): void {
    const eventData: SharedLinkOpenEvent = {
      viewer_type: isAuthenticated ? 'authenticated' : 'guest',
      content_id: contentId,
      content_type: contentType,
      content_name: contentName,
      timestamp: Date.now(),
      referrer: document.referrer
    };

    this.pushEvent(AnalyticsEventType.SHARED_LINK_OPEN, eventData);
  }

    /**
   * TRACCIAMENTO CONDIVISIONE COLLEZIONE
   */
  trackWishlistShare(wishlist: Wishlist, shareLink: string): void {
    const eventData = {
      wishlist_id: wishlist.uuid,
      wishlist_name: wishlist.name,
      is_public: wishlist.isPublic,
      items_count: wishlist.productsRef?.length || 0,
      share_link: shareLink,
      timestamp: Date.now()
    };

    this.pushEvent(AnalyticsEventType.WISHLIST_SHARE, eventData);
  }

	/**
	 * Abilita/disabilita debug mode
	 */
	setDebugMode(enabled: boolean): void {
		this.config.debug = enabled;
	}
}
